/**
 * Permissions & Access — assignment and activation workflow.
 */

import {
  buildAccessSummaryLabel,
  compareModulePermissions,
  formatCustomisedAccessDisplay,
  getDefaultAccessLabelForPosition,
  getPermissionPresetForPosition,
  getPresetNameForPosition,
  moduleHasAnyPermission,
  normalizeModulePermissions,
} from './permissionsConfig';
import {
  ACCESS_DURATION,
  findActivePermissionConfig,
  formatExtendedModuleActionsSummary,
  isConfigurationModule,
} from './permissionsAccessMatrix';
import { recordChangeHistory } from './changeHistoryStore';
import { CHANGE_HISTORY_DISPLAY } from './changeHistoryEntry';

const TEAM_MEMBER_STATUS_ACTIVE = 'Active';
const TEAM_MEMBER_STATUS_INACTIVE = 'Inactive';

function emitPermissionsAccessHistory(member, { changeType, activityDetails, actorName, stamp }) {
  if (!member?.id) return;
  try {
    recordChangeHistory({
      sourceChangeRef: `configuration_permissions::${member.id}::${changeType}::${stamp}`,
      sourceModuleRef: 'configuration_permissions',
      sourceItemRef: member.id,
      module: 'Configuration — Permissions & Access',
      changedItem: member.name || member.id,
      itemId: member.id,
      changeType,
      activityOnly: true,
      activityDetails,
      changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
      changedBy: { name: actorName, role: 'Platform Owner' },
      changedOnIso: stamp,
      accountRef: member.id,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
}

function generateTemporaryPassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const special = '@$!%*?&';
  const pick = (set) => set[Math.floor(Math.random() * set.length)];
  const base = [pick(upper), pick(lower), pick(digits), pick(special)];
  const all = upper + lower + digits + special;
  while (base.length < 12) base.push(pick(all));
  for (let i = base.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [base[i], base[j]] = [base[j], base[i]];
  }
  return base.join('');
}

export const INITIAL_ASSIGNMENT_SUCCESS =
  'Access saved. Pending Access became Active and the initial password is shown once.';
export const EDIT_ASSIGNMENT_SUCCESS = 'Access updated.';
export const ALL_ACCESS_REMOVED_SUCCESS =
  'All Access Removed. Account status is Inactive and live access has stopped.';
export const SAVE_FAILURE_MESSAGE = 'Unable to save access. Review the form and try again.';

/** @deprecated Confirm popup removed — review is in-form. */
export const SAVE_CONFIRM_TITLE = 'Review Access';
export const SAVE_CONFIRM_LABEL = 'Save';

export function collectAccessSaveErrors(form, { configs = [], isInitial = false } = {}) {
  const errors = {};
  if (!form.memberInternalId && !form.teamMemberId) {
    errors.teamMember = 'Select Team Member.';
  }

  const granted = normalizeModulePermissions(form.modulePermissions).filter(moduleHasAnyPermission);
  const hasRoleSelection = Boolean(form.teamType && form.department && form.jobRole);

  if (isInitial && !granted.length) {
    errors.modulePermissions = 'Grant at least one valid module.';
  }

  if (hasRoleSelection || (isInitial && !granted.length)) {
    if (!form.teamType) errors.teamType = 'Select Team Type.';
    if (!form.department) errors.department = 'Select Department.';
    if (!form.jobRole) errors.jobRole = 'Select Job Role.';
  }

  if (hasRoleSelection) {
    const matching = findActivePermissionConfig(configs, {
      teamType: form.teamType,
      department: form.department,
      jobRole: form.jobRole,
    });
    const unchangedInactiveRef =
      form.permissionConfigId &&
      (configs || []).some(
        (c) => c.id === form.permissionConfigId && c.status !== 'Active',
      );
    if (!matching && !unchangedInactiveRef && form.permissionConfigId !== form.savedPermissionConfigId) {
      if (!matching) {
        errors.jobRole = 'Select a Job Role with exactly one matching Active Permission Configuration.';
      }
    }
  }

  granted.forEach((row) => {
    if (!row.view) {
      errors.modulePermissions = 'Every selected module must include View.';
    }
  });

  (form.configurationMaintenance || []).forEach((block) => {
    if (!block?.module) return;
    if (block.duration === ACCESS_DURATION.TEMPORARY) {
      if (!block.expiry) {
        errors[`maintenance_${block.module}`] = 'Enter Access Expiry for Temporary access.';
      } else {
        const when = new Date(block.expiry);
        if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
          errors[`maintenance_${block.module}`] = 'Access Expiry must be a future date and time.';
        }
      }
    }
  });

  return errors;
}

export function buildAccessPatchFromForm(form) {
  const preset = getPermissionPresetForPosition(form.jobRole || form.position);
  const modulePermissions = normalizeModulePermissions(form.modulePermissions);
  const diff = compareModulePermissions(
    form.presetModulePermissions || preset?.modulePermissions || [],
    modulePermissions,
  );
  const defaultAccessLabel =
    form.defaultAccessLabel ||
    preset?.defaultAccessLabel ||
    getDefaultAccessLabelForPosition(form.jobRole || form.position);
  const permissionPreset =
    form.permissionPreset ||
    preset?.presetName ||
    getPresetNameForPosition(form.jobRole || form.position);
  const hasCustomisedAccess = diff.hasCustomisedAccess || Boolean(form.hasCustomisedAccess);
  const team = form.department || form.team || '';
  const position = form.jobRole || form.position || '';
  const permissionsAccessLabel = buildAccessSummaryLabel({
    position,
    defaultAccessLabel,
    hasCustomisedAccess,
    customPermission: hasCustomisedAccess ? form.customPermission || null : null,
  });

  return {
    team,
    position,
    jobTitle: position,
    teamType: form.teamType || '',
    department: form.department || team,
    jobRole: position,
    platformRole: 'Team Member',
    permissionConfigId: form.permissionConfigId || null,
    permissionPreset,
    defaultAccessLabel,
    dashboard: form.dashboard || preset?.dashboard || '',
    hasCustomisedAccess,
    customPermission: hasCustomisedAccess ? form.customPermission || 'Customised' : null,
    permissionsAdded: diff.added,
    permissionsRemoved: diff.removed,
    modulePermissions,
    configurationMaintenance: (form.configurationMaintenance || []).map((b) => ({ ...b })),
    permissionName: defaultAccessLabel,
    permissionsAccessLabel,
  };
}

export function applyInitialAccessAssignment(member, form, { actorName = 'Platform Owner' } = {}) {
  const now = new Date().toISOString();
  const patch = buildAccessPatchFromForm(form);
  const temporaryPassword = generateTemporaryPassword();

  const nextMember = {
    ...member,
    ...patch,
    status: TEAM_MEMBER_STATUS_ACTIVE,
    requirePasswordChange: true,
    lastUpdated: now,
    lastUpdatedBy: actorName,
  };
  emitPermissionsAccessHistory(nextMember, {
    changeType: 'Access Assigned',
    activityDetails: 'Initial access assignment saved. Protected credential: Updated.',
    actorName,
    stamp: now,
  });
  return {
    member: nextMember,
    temporaryPassword,
    isInitialAssignment: true,
  };
}

export function applyAccessConfigurationUpdate(member, form, { actorName = 'Platform Owner' } = {}) {
  const now = new Date().toISOString();
  const patch = buildAccessPatchFromForm(form);
  const granted = normalizeModulePermissions(form.modulePermissions).filter(moduleHasAnyPermission);
  const allAccessRemoved = granted.length === 0;
  const nextStatus = allAccessRemoved
    ? TEAM_MEMBER_STATUS_INACTIVE
    : member.status === 'Pending Access'
      ? TEAM_MEMBER_STATUS_ACTIVE
      : member.status;

  const nextMember = {
    ...member,
    ...patch,
    ...(allAccessRemoved
      ? {
          team: 'Not Assigned',
          position: 'Not Assigned',
          jobTitle: 'Not Assigned',
          teamType: '',
          department: 'Not Assigned',
          jobRole: 'Not Assigned',
          permissionPreset: 'Not Assigned',
          defaultAccessLabel: 'Not Assigned',
          dashboard: 'Not Assigned',
          permissionsAccessLabel: 'Not Assigned',
          permissionName: 'Not Assigned',
          configurationMaintenance: [],
        }
      : {}),
    status: nextStatus,
    lastUpdated: now,
    lastUpdatedBy: actorName,
  };
  emitPermissionsAccessHistory(nextMember, {
    changeType: allAccessRemoved ? 'Access Removed' : 'Access Updated',
    activityDetails: allAccessRemoved
      ? 'All module access removed; member set Inactive'
      : 'Permission and access configuration saved',
    actorName,
    stamp: now,
  });
  return {
    member: nextMember,
    temporaryPassword: null,
    isInitialAssignment: false,
    allAccessRemoved,
  };
}

export function mergeMembersAfterAccessSave(members, result) {
  const updated = result.member;
  return (members || []).map((m) => (m.id === updated.id ? updated : m));
}

export function buildConfirmationSummary(form, { previousForm = null } = {}) {
  const modulePermissions = normalizeModulePermissions(form.modulePermissions);
  const granted = modulePermissions.filter(moduleHasAnyPermission);
  const diff = compareModulePermissions(
    form.presetModulePermissions || [],
    modulePermissions,
  );

  return {
    teamMember: form.name
      ? `${form.name}${form.workEmail ? ` (${form.workEmail})` : ''}`
      : '—',
    memberId: form.teamMemberId || '—',
    teamType: form.teamType || 'Not Assigned',
    department: form.department || form.team || 'Not Assigned',
    jobRole: form.jobRole || form.position || 'Not Assigned',
    permissionPreset: form.permissionPreset || 'Not Assigned',
    defaultAccess: form.defaultAccessLabel || 'Not Assigned',
    dashboard: form.dashboard || 'Not Assigned',
    customisedAccess: formatCustomisedAccessDisplay({
      hasCustomisedAccess: diff.hasCustomisedAccess || form.hasCustomisedAccess,
      defaultAccessLabel: form.defaultAccessLabel || '',
      moduleLabels: diff.changedModules,
    }),
    permissionsAdded: diff.added,
    permissionsRemoved: diff.removed,
    modulePermissions: granted,
    moduleSummaries: granted.map((row) => ({
      module: row.module,
      label: row.module,
      actions: formatExtendedModuleActionsSummary(row),
      scope: row.dataAccessScope,
      isConfiguration: isConfigurationModule(row.module),
    })),
    configurationMaintenance: form.configurationMaintenance || [],
    accountStatus: form.accountStatus || '',
    showInitialCredentialNotice: form.accountStatus === 'Pending Access',
    previous: previousForm
      ? {
          teamType: previousForm.teamType,
          department: previousForm.department || previousForm.team,
          jobRole: previousForm.jobRole || previousForm.position,
          permissionPreset: previousForm.permissionPreset,
          defaultAccess: previousForm.defaultAccessLabel,
        }
      : null,
  };
}
